import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Tabs,
  Typography,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory2';
import RecordIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import SchoolIcon from '@mui/icons-material/School';
import DataIcon from '@mui/icons-material/BarChart';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import MessagesIcon from '../Navbar/icons/MessagesIcon';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import mapitLogo from '../../utilities/dummy_assets/mapITlogo.png';
import mapitfullLogo from '../../utilities/dummy_assets/svgs/Mapiot_whitetext.svg';
import MSISLogo from '../../utilities/dummy_assets/svgs/LOGO_MSIS_Dark.svg';
import smallMSISLogo from '../../utilities/dummy_assets/svgs/LOGO_MSIS_Dark_Small.svg';
import expandRight from '../../utilities/dummy_assets/Expand_left_double.png';
import expandLeft from '../../utilities/dummy_assets/Expand_left_double (1).png';
import { logoutUser } from '../../redux/slices/userSlice';
import GlobalWebSocketService from '../../utilities/api/IMS/GlobalWebSocketService';
import SimulationAPIs from '../../utilities/api/simulation/SimulationAPIs';

// ==================== TABS CONFIG ====================
const defaultTabsConfig = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
  { label: 'Training', icon: <SchoolIcon />, path: '/training' },
  { label: 'Project', icon: <FolderOpenOutlinedIcon />, path: '/project' },
  { label: 'Records', icon: <RecordIcon />, path: '/records' },
];

// New AI Layer Items
const aiLayerItems = [
  {
    label: 'Chat & Copilot',
    icon: <MessagesIcon sx={{ height: '32px', width: '32px' }} />,
    path: '/messages',
  },
  {
    label: 'AI Copilot',
    icon: <AutoAwesomeIcon />,
    path: '/ai-copilot',
  },
];

const bottomItems = [
  { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  { label: 'Logout', icon: <LogoutIcon />, path: '/login', isLogout: true },
];

// Define the admin tabsConfig
const adminTabsConfig = [
  { label: 'TL Data', icon: <DataIcon />, path: '/tl-data' },
];

const CustomTab = ({ label, icon, selected, collapsed }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: collapsed ? 'center' : 'flex-start',
      alignItems: 'center',
      width: '100%',
      padding: collapsed ? '5px 4px' : '5px 16px',
      borderRadius: '8px',
      backgroundColor: selected ? '#ffffff' : 'transparent',
      color: selected ? '#000' : '#fff',
      gap: collapsed ? 0 : 2,
      transition: 'all 0.3s ease',
      position: 'relative',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.9)',
        color: '#000',
        '& .MuiSvgIcon-root': { color: '#000' },
      },
    }}
  >
    <Box sx={{ minWidth: 32, textAlign: 'center' }}>{icon}</Box>
    {!collapsed && (
      <>
        <Typography
          sx={{
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {label}
        </Typography>
      </>
    )}
  </Box>
);

// ────────────────────────────────────────────────
//  Sidebar Content (logo + tabs + footer)
// ────────────────────────────────────────────────
const SidebarContent = ({
  collapsed,
  setCollapsed,
  mobile = false,
  onClose,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(state => state.user.user);
  const [isSimulation, setIsSimulation] = useState(false);
  const [selectedTab, setSelectedTab] = useState(() => {
    const tabs =
      user?.user?.TL_name === 'MBF Admin' ? adminTabsConfig : defaultTabsConfig;
    const idx = tabs.findIndex(t => t.path === location.pathname);
    return idx !== -1 ? idx : 0;
  }); // Initializing from current path

  useEffect(() => {
    const checkSimulation = async () => {
      try {
        const status = await SimulationAPIs.getStatus();
        setIsSimulation(status.is_simulation);
      } catch (error) {
        console.error('Error fetching simulation status:', error);
      }
    };
    checkSimulation();
  }, []);

  const isMBFAdmin = user?.user?.TL_name === 'MBF Admin';

  const baseTabs = isMBFAdmin ? adminTabsConfig : defaultTabsConfig;

  const tabsConfig = isSimulation
    ? baseTabs
    : baseTabs.filter(t => t.label !== 'Live Feed');

  const allTabs = [...tabsConfig, ...aiLayerItems, ...bottomItems];

  useEffect(() => {
    const idx = allTabs.findIndex(t => t.path === location.pathname);
    if (idx !== -1) setSelectedTab(idx);
  }, [location.pathname]);

  const handleClick = index => {
    const tab = allTabs[index];
    if (tab?.isLogout) {
      GlobalWebSocketService.disconnect();
      dispatch(logoutUser());
      navigate('/login');
      return;
    }
    navigate(tab.path);
    if (mobile) onClose?.();
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#212121',
        color: 'white',
      }}
    >
      {/* Header with logo + collapse button (desktop only) */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: collapsed ? 'center' : 'flex-start',
          flexDirection: collapsed ? 'column-reverse' : 'row',
          justifyContent: mobile ? 'space-between' : 'center',
          gap: '15px',
          position: 'relative',
          ...(!(mobile || collapsed) && { paddingRight: 0 }),
        }}
      >
        <Box
          component="a"
          href="https://makerbhavanfoundation.org/"
          sx={{ display: 'block', flex: 1, textAlign: 'center' }}
        >
          <Box
            component="img"
            src={collapsed && !mobile ? smallMSISLogo : MSISLogo}
            alt="MSIS"
            sx={{
              height: { xs: 80, md: collapsed ? 80 : 100 },
              width: 'auto',
              maxWidth: '90%',
            }}
          />
        </Box>

        {!mobile && (
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              color: '#fff',
              borderRadius: collapsed ? '8px' : '8px 0px 0px 8px',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            <Box
              component="img"
              src={collapsed ? expandRight : expandLeft}
              sx={{ width: 20, height: 20 }}
            />
          </IconButton>
        )}
      </Box>

      {/* Navigation */}
      <Tabs
        orientation="vertical"
        variant="scrollable"
        scrollButtons={false}
        value={false}
        onChange={() => {}}
        sx={{
          width: '100%',
          px: { xs: 0.5, md: 1 },
          '& .MuiTab-root': {
            alignItems: 'center',
            justifyContent: 'center',
            textTransform: 'none',
            margin: { xs: '2px 0', md: '3px 0' },
            padding: { xs: '4px 2px', sm: '6px 4px', md: '8px 6px' },
            backgroundColor: 'transparent',
            minWidth: 'auto',
            width: '100%',
            minHeight: { xs: '40px', sm: '44px', md: '48px' },
            '&:hover': {
              backgroundColor: 'white',
              color: 'black',
            },
          },
          '& .MuiTabs-flexContainer': {
            flexDirection: 'column',
          },
          flex: 1,
        }}
      >
        {tabsConfig.map((tab, i) => (
          <Box
            key={tab.label}
            onClick={() => handleClick(i)}
            sx={{ cursor: 'pointer', my: 0.5 }}
          >
            <CustomTab
              label={tab.label}
              icon={React.cloneElement(tab.icon, {
                sx: {
                  color: selectedTab === i ? 'black' : 'white',
                  fontSize: { xs: 24, md: 28 },
                },
              })}
              selected={selectedTab === i}
              collapsed={collapsed && !mobile}
            />
          </Box>
        ))}
        <>
            {/* AI LAYER Section + Divider */}
            {!collapsed && (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                sx={{ px: 2, mt: 3, mb: 1 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#90a4ae',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                  }}
                >
                  AI LAYER
                </Typography>

                <Box
                  sx={{
                    ml: 1,
                    bgcolor: '#3b82f6',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    px: 1,
                    py: 0.5,
                    borderRadius: '999px',
                  }}
                >
                  <AutoAwesomeIcon fontSize="10px" /> {'  '}
                  New
                </Box>
              </Box>
            )}

            <Divider sx={{ mx: 2, bgcolor: 'rgba(255,255,255,0.1)', my: 1 }} />

            {aiLayerItems.map((tab, idx) => {
              const globalIndex = tabsConfig.length + idx;
              return (
                <Box
                  key={tab.label}
                  onClick={() => handleClick(globalIndex)}
                  sx={{ cursor: 'pointer', my: 0.5 }}
                >
                  <CustomTab
                    label={tab.label}
                    icon={React.cloneElement(tab.icon, {
                      sx: {
                        color: selectedTab === globalIndex ? 'black' : 'white',
                        fontSize: { xs: 24, md: 28 },
                      },
                    })}
                    selected={selectedTab === globalIndex}
                    collapsed={collapsed && !mobile}
                  />
                </Box>
              );
            })}
            <Divider sx={{ mx: 2, bgcolor: 'rgba(255,255,255,0.1)', my: 1 }} />

            {/* Bottom Items */}
            {bottomItems.map((tab, idx) => {
              const globalIndex =
                tabsConfig.length + aiLayerItems.length + idx;
              return (
                <Box
                  key={tab.label}
                  onClick={() => handleClick(globalIndex)}
                  sx={{ cursor: 'pointer', my: 0.5 }}
                >
                  <CustomTab
                    label={tab.label}
                    icon={React.cloneElement(tab.icon, {
                      sx: {
                        color: selectedTab === globalIndex ? 'black' : 'white',
                        fontSize: { xs: 24, md: 28 },
                      },
                    })}
                    selected={selectedTab === globalIndex}
                    collapsed={collapsed && !mobile}
                  />
                </Box>
              );
            })}
        </>
      </Tabs>

      {/* Footer - powered by */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: '2px', md: '4px' },
          p: { xs: '5px', sm: '8px', md: '10px' },
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: '#FFF',
            fontFamily: 'Roboto',
            fontSize: { xs: '10px', sm: '12px', md: '14px' },
            fontWeight: 400,
            fontStyle: 'italic',
            textTransform: 'lowercase',
            opacity: collapsed ? 0 : 0.8,
            height: collapsed ? 0 : 'auto',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          powered by
        </Typography>
        <Box
          sx={{
            width: collapsed ? '36px' : '90px',
            height: collapsed ? '36px' : '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <img
            src={collapsed ? mapitLogo : mapitfullLogo}
            alt="MapIT Logo"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// ────────────────────────────────────────────────
//  Main Menu Component (now responsive)
// ────────────────────────────────────────────────
const MenuComponent = ({
  collapsed = false,
  setCollapsed = () => {},
  isMobile,
  mobileOpen = false,
  handleDrawerToggle = () => {},
}) => {
  const theme = useTheme();

  // Desktop permanent sidebar
  const desktopSidebar = (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? { md: 80, lg: 90 } : { md: 200, lg: 220 },
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: collapsed ? { md: 80, lg: 90 } : { md: 200, lg: 220 },
          boxSizing: 'border-box',
          bgcolor: '#212121',
          borderRight: 'none',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      <SidebarContent collapsed={collapsed} setCollapsed={setCollapsed} />
    </Drawer>
  );

  // Mobile temporary drawer
  const mobileDrawer = (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      ModalProps={{ keepMounted: true }} // better mobile performance
      sx={{
        display: { xs: 'block', md: 'none' },
        [`& .MuiDrawer-paper`]: {
          width: 220,
          boxSizing: 'border-box',
        },
      }}
    >
      <SidebarContent
        collapsed={false} // always full on mobile
        mobile
        onClose={handleDrawerToggle}
      />
    </Drawer>
  );

  {/* Drawers */}
  return isMobile ? mobileDrawer : desktopSidebar;
};

export default MenuComponent;
